import { OrionEclipseAccountDetailModel, OrionEclipsePortfolioDetailModel } from 'src/app/core/clients/generated/client';

/** Sleeved: any one restricted sleeve, or a portfolio block that covers them all. */
export const sleevedTradeRestrictionMessage =
  'One or more sleeves in this account are currently restricted in the trade system. '
  + 'You may submit the request, but it will require additional review by the Trade Team.';

/** Unsleeved: the single Eclipse row *is* the account, so the account itself is what is restricted. */
export const unsleevedTradeRestrictionMessage =
  'This account is currently restricted in the trade system. '
  + 'You may submit the request, but it will require additional review by the Trade Team.';

/**
 * The trade-restriction warning for an account, or null when there is nothing to say.
 *
 * A restriction on the portfolio, or on any one sleeve, negates our ability to update that sleeve —
 * so the advisor is warned before building a request the Trade Team may not be able to action. It is
 * a warning, not a gate: they may still submit (ACMTECH-904).
 *
 * Pure so it can be tested without standing up the modal, which has ten-plus injected dependencies.
 *
 * @param sleeves The Eclipse rows for the account, one per sleeve plus the custodial parent.
 * @param portfolio The account's Eclipse portfolio, or null when it could not be read.
 * @returns The message to show, or null to show nothing.
 */
export function deriveTradeRestrictionMessage(
  sleeves: OrionEclipseAccountDetailModel[] | null | undefined,
  portfolio: OrionEclipsePortfolioDetailModel | null | undefined,
): string | null {
  // No rows means we could not establish anything — degrade to silence rather than to
  // "unrestricted". A missing warning that looks like a clean account is the one outcome worse
  // than no feature at all.
  if (!sleeves?.length) return null;

  // General.doNotTrade is the portfolio-wide flag. NOT Issues.doNotTrade, which is an issue
  // *count*, and not Connect's per-sleeve doNotTrade, which reads true on Custom Holdings sleeves
  // that trade perfectly well — using that one would warn on every single reallocation.
  const portfolioRestricted = portfolio?.general?.doNotTrade === true;
  const anySleeveRestricted = sleeves.some((s) => s.isDoNotBuySell);

  if (!portfolioRestricted && !anySleeveRestricted) return null;

  // Row count is the discriminator, never `isSleeve` — Eclipse reports isSleeve: 1 on every row
  // including the bare custodial parent, so it distinguishes nothing. One row means the account is
  // unsleeved and that row IS the account.
  return sleeves.length === 1 ? unsleevedTradeRestrictionMessage : sleevedTradeRestrictionMessage;
}
